import { extractYoutubeVideoId } from '@/lib/comment-embed-pipeline'
import type { ShortFeedItem } from '@/lib/news-types'
import { absoluteUrl } from '@/lib/site-seo'

/** TikTok video id veya kısa kod */
export function extractTiktokVideoId(url: string): string | null {
  const u = url.trim()
  if (!u) return null
  const video = /tiktok\.com\/@[\w.]+\/video\/(\d{8,25})/i.exec(u)
  if (video?.[1]) return video[1]
  const vm = /vm\.tiktok\.com\/([\w-]+)/i.exec(u)
  if (vm?.[1]) return vm[1]
  const short = /tiktok\.com\/t\/([\w-]+)/i.exec(u)
  if (short?.[1]) return short[1]
  return null
}

export function isYoutubeVideoSlug(raw: string): boolean {
  return /^[a-zA-Z0-9_-]{11}$/.test(raw)
}

export function isNumericShortSlug(raw: string): boolean {
  return /^\d+$/.test(raw) && parseInt(raw, 10) > 0
}

/** @deprecated Eski yt-{dbId} slug */
export function isYoutubeShortRouteSlug(raw: string): boolean {
  return /^yt-\d+$/i.test(raw.trim())
}

export function isTiktokVideoSlug(raw: string): boolean {
  return /^\d{8,25}$/.test(raw) || /^[\w-]{6,24}$/.test(raw)
}

export function parseShortsRouteSlug(raw: string | undefined): string | null {
  if (!raw) return null
  const trimmed = raw.trim()
  if (!trimmed || trimmed.length > 64) return null
  if (/^yt-\d+$/i.test(trimmed)) return trimmed.toLowerCase()
  if (/^\d{15,25}$/.test(trimmed)) return trimmed
  if (isYoutubeVideoSlug(trimmed)) return trimmed
  if (isNumericShortSlug(trimmed)) return trimmed
  if (/^[\w-]{6,24}$/.test(trimmed)) return trimmed
  return null
}

export function parseYoutubeShortRouteId(raw: string | undefined): number | undefined {
  if (!raw) return undefined
  const trimmed = raw.trim()
  if (!/^\d+$/.test(trimmed)) return undefined
  const id = parseInt(trimmed, 10)
  return Number.isFinite(id) && id > 0 ? id : undefined
}

type ShortSlugItem = Pick<ShortFeedItem, 'id' | 'source' | 'youtube' | 'video' | 'x'> & {
  slug?: string
}

export function getYoutubeShortKey(item: ShortSlugItem): string | null {
  const yt = item.youtube?.trim()
  if (yt && isYoutubeVideoSlug(yt)) return yt
  const slug = item.slug?.trim()
  if (slug && isYoutubeVideoSlug(slug)) return slug
  if (slug && /^yt-(\d+)$/i.test(slug)) return slug.toLowerCase()
  return null
}

export function getShortFeedKey(item: ShortSlugItem): string {
  const ytKey = getYoutubeShortKey(item)
  if (item.source === 'youtube' && ytKey) return ytKey
  if (item.slug?.trim()) return item.slug.trim()
  return String(item.id)
}

export function getShortsSlugForItem(item: ShortSlugItem): string {
  return getShortFeedKey(item)
}

export function getShortsHrefForItem(item: ShortSlugItem): string {
  const ytKey = getYoutubeShortKey(item)
  if (item.source === 'youtube' && ytKey && isYoutubeVideoSlug(ytKey)) {
    return `/shorts/${ytKey}`
  }
  if (item.source === 'youtube' && item.slug?.match(/^yt-(\d+)$/i)) {
    return `/shorts/${item.slug}`
  }
  return `/shorts/${item.id}`
}

export function getShortsCanonicalUrlForItem(item: ShortSlugItem): string {
  return absoluteUrl(getShortsHrefForItem(item))
}

export function formatShortCount(value?: number | null): string {
  const n = Number(value) || 0
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`
  if (n >= 10_000) return `${Math.round(n / 1000)}K`
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}K`
  return String(n)
}

export { extractYoutubeVideoId }
